// LoadingSpinner Component - Reusable loading indicator
// Supports multiple sizes and inline/fullscreen modes
// Accessible with proper ARIA attributes

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  message?: string;
  className?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
};

export function LoadingSpinner({
  size = 'md',
  fullScreen = false,
  message,
  className = '',
}: LoadingSpinnerProps) {
  const spinner = (
    <div
      className={`inline-block animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label={message ?? 'Loading'}
    >
      <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
        Loading...
      </span>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-sky-600 dark:text-sky-400 mb-4">{spinner}</div>
          {message && (
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <div className="text-sky-600 dark:text-sky-400">{spinner}</div>
      {message && (
        <span className="ml-3 text-gray-600 dark:text-gray-400 text-sm">
          {message}
        </span>
      )}
    </div>
  );
}

// Skeleton loader for content placeholders
interface SkeletonProps {
  className?: string;
  animate?: boolean;
}

export function Skeleton({ className = '', animate = true }: SkeletonProps) {
  return (
    <div
      className={`bg-gray-200 dark:bg-gray-700 rounded ${
        animate ? 'animate-pulse' : ''
      } ${className}`}
    />
  );
}

// Card skeleton loader
export function CardSkeleton({ animate = true }: { animate?: boolean }) {
  return (
    <div className="card">
      <div className="card-header">
        <Skeleton className="h-6 w-1/3 mb-2" animate={animate} />
        <Skeleton className="h-4 w-2/3" animate={animate} />
      </div>
      <div className="card-content space-y-3">
        <Skeleton className="h-4 w-full" animate={animate} />
        <Skeleton className="h-4 w-5/6" animate={animate} />
        <Skeleton className="h-4 w-4/6" animate={animate} />
      </div>
    </div>
  );
}

// Profile skeleton loader
export function ProfileSkeleton({ animate = true }: { animate?: boolean }) {
  return (
    <div className="flex items-start space-x-4">
      <Skeleton className="h-16 w-16 rounded-full" animate={animate} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-32" animate={animate} />
        <Skeleton className="h-4 w-48" animate={animate} />
        <div className="flex space-x-2 mt-3">
          <Skeleton className="h-6 w-16 rounded-full" animate={animate} />
          <Skeleton className="h-6 w-20 rounded-full" animate={animate} />
        </div>
      </div>
    </div>
  );
}
